import React, { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DashboardAppShell from '../components/DashboardAppShell';
import { useApp } from '../context/AppContext';

const FONT_SERIF = "'Playfair Display', Georgia, serif";
const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";

const ROUTES = ['Injection', 'Oral', 'Patch', 'Topical', 'Sublingual', 'Other'];
const UNITS = ['IU', 'mg', 'mcg', 'mL', 'units'];

const MED_COLORS = ['#FBEAF0', '#E1F5EE', '#EEEDFE', '#FFF5F0', '#E6F1FB', '#FCE8F3'];

function medColor(name) {
  let n = 0;
  const s = String(name || '').toLowerCase();
  for (let i = 0; i < s.length; i++) n += s.charCodeAt(i);
  return MED_COLORS[n % MED_COLORS.length];
}

const btnPrimary = { padding: '10px 20px', background: 'var(--pink)', color: 'white', border: 'none', borderRadius: 999, cursor: 'pointer', fontSize: 14 };
const btnSecondary = { padding: '8px 16px', background: 'var(--sand)', color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 999, cursor: 'pointer', fontSize: 13 };
const inputStyle = { padding: 10, borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, width: '100%' };

export default function Medications() {
  const navigate = useNavigate();
  const {
    user,
    journey,
    activeCycle,
    ensureActiveCycle,
    addMedication,
    updateMedication,
    removeMedication,
    addReminder,
    getMedicationDoseLog,
    logDoseTaken,
    addSideEffect,
    getSideEffects,
    setNovaDraft,
  } = useApp();

  const [showAddForm, setShowAddForm] = useState(false);
  const [historyFilter, setHistoryFilter] = useState({ cycle: 'all', from: '', to: '' });
  const [showSideEffectForm, setShowSideEffectForm] = useState(false);
  const [editingReminderId, setEditingReminderId] = useState(null);

  const addFormDefault = { name: '', dose: '', unit: 'IU', frequency: '', route: 'Injection', purpose: '', startDate: '', reminderTime: '09:00' };
  const [addForm, setAddForm] = useState(addFormDefault);
  const sideEffectForm = useState({ medicationId: '', medicationName: '', description: '', severity: 3 })[0];
  const [sideEffectFormState, setSideEffectFormState] = useState(sideEffectForm);

  const chapter = (user?.journeyType || 'fertility').toLowerCase();
  const isFertility = chapter === 'fertility' || chapter === 'ivf';

  React.useEffect(() => {
    ensureActiveCycle?.();
  }, [ensureActiveCycle]);

  const activeCycleId = journey?.activeCycleId;
  const cycles = journey?.cycles || [];
  const activeMeds = useMemo(() => {
    const cycle = cycles.find((c) => c.id === activeCycleId);
    return cycle?.medications || [];
  }, [cycles, activeCycleId]);

  const pastCycles = useMemo(() => cycles.filter((c) => c.id !== activeCycleId), [cycles, activeCycleId]);

  const todayStr = new Date().toISOString().slice(0, 10);
  const sideEffects = getSideEffects?.(user) || [];

  function handleAddMedication(e) {
    e?.preventDefault();
    if (!addForm.name?.trim() || !activeCycleId) return;
    addMedication?.(activeCycleId, {
      name: addForm.name.trim(),
      dose: addForm.dose,
      units: addForm.unit,
      frequency: addForm.frequency,
      route: addForm.route,
      purpose: addForm.purpose,
      startDate: addForm.startDate,
      startDay: addForm.startDate ? String(Math.ceil((new Date(addForm.startDate) - new Date(activeCycle?.startedAt || addForm.startDate)) / 86400000) || 1) : '1',
      reminderTime: addForm.reminderTime,
    });
    if (addForm.reminderTime) {
      addReminder?.(activeCycleId, {
        title: addForm.name,
        time: addForm.reminderTime,
        linkedMedicationId: null,
      });
    }
    setAddForm(addFormDefault);
    setShowAddForm(false);
  }

  function openNovaForMed(medName) {
    setNovaDraft?.(`Can you explain ${medName} to me? What should I know about taking it?`);
    navigate('/nova');
  }

  const cycleStart = activeCycle?.startedAt ? new Date(activeCycle.startedAt) : new Date();
  const protocolDays = useMemo(() => {
    const days = [];
    const medsByDay = {};
    activeMeds.forEach((m) => {
      const startDay = Number(m.startDay) || 1;
      const endDay = Number(m.endDay) || 14;
      for (let d = startDay; d <= endDay; d++) {
        if (!medsByDay[d]) medsByDay[d] = [];
        medsByDay[d].push(m);
      }
    });
    const maxDay = Math.max(14, ...Object.keys(medsByDay).map(Number), ...Object.values(medsByDay).flat().map((m) => Number(m.endDay) || 14));
    for (let d = 1; d <= maxDay; d++) {
      days.push({ day: d, meds: medsByDay[d] || [] });
    }
    return days;
  }, [activeMeds]);

  const filteredHistory = useMemo(() => {
    let items = pastCycles.flatMap((c) => (c.medications || []).map((m) => ({ ...m, cycleName: c.name, cycleId: c.id, startedAt: c.startedAt })));
    if (historyFilter.cycle !== 'all') {
      const c = pastCycles.find((x) => x.id === historyFilter.cycle);
      if (c) items = (c.medications || []).map((m) => ({ ...m, cycleName: c.name, cycleId: c.id, startedAt: c.startedAt }));
    }
    if (historyFilter.from) items = items.filter((i) => (i.startedAt || '') >= historyFilter.from);
    if (historyFilter.to) items = items.filter((i) => (i.startedAt || '') <= historyFilter.to);
    return items.sort((a, b) => (b.startedAt || '').localeCompare(a.startedAt || ''));
  }, [pastCycles, historyFilter]);

  return (
    <DashboardAppShell
      fontFamily={FONT_BODY}
      rightColumnGridTrack="220px"
      rightColumn={(
      <aside style={{ padding: 24, borderLeft: '1px solid var(--border)', background: 'white' }}>
        <h4 style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', marginBottom: 12, textTransform: 'uppercase' }}>Quick links</h4>
        <button type="button" onClick={() => openNovaForMed('my medications')} style={{ ...btnPrimary, width: '100%', marginBottom: 8 }}>
          Ask Nova about my medications
        </button>
        <Link to="/health" style={{ display: 'block', fontSize: 14, color: 'var(--pink)', marginBottom: 8 }}>Health record →</Link>
      </aside>
      )}
    >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
          <h1 style={{ fontFamily: FONT_SERIF, fontSize: 24, color: 'var(--text)' }}>Medications</h1>
          <button type="button" onClick={() => setShowAddForm(true)} style={btnPrimary}>
            Add medication
          </button>
        </div>

        {/* ACTIVE MEDICATIONS */}
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 16 }}>Active medications</h2>
          {activeMeds.length === 0 ? (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
              No active medications. Add your first medication to get started.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {activeMeds.map((m) => {
                const log = getMedicationDoseLog?.(m.id, user) || {};
                const takenToday = log[todayStr];
                return (
                  <div key={m.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                      <div>
                        <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--text)' }}>{m.name}</div>
                        <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>
                          {m.dose} {m.units} · {m.frequency || m.schedule || '—'} · {m.route || '—'}
                        </div>
                        {m.purpose && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 2 }}>Purpose: {m.purpose}</div>}
                      </div>
                      <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 999, background: medColor(m.name), color: 'var(--text)' }}>{m.route || 'Med'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 12, fontSize: 13 }}>
                      <span>Start: {m.startDate || m.startDay ? `Day ${m.startDay}` : '—'}</span>
                      <span>End: {m.endDate || m.endDay ? `Day ${m.endDay}` : '—'}</span>
                    </div>
                    <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!takenToday} onChange={(e) => e.target.checked && logDoseTaken?.(m.id, todayStr, new Date().toTimeString().slice(0, 5))} />
                        Today's dose taken {takenToday && <span style={{ color: 'var(--muted)' }}>@ {takenToday}</span>}
                      </label>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <span style={{ fontSize: 12, color: 'var(--muted)' }}>Reminder: </span>
                      {editingReminderId === m.id ? (
                        <span>
                          <input type="time" value={m.reminderTime || '09:00'} onChange={(e) => updateMedication?.(activeCycleId, m.id, { reminderTime: e.target.value })} style={{ ...inputStyle, width: 100, display: 'inline-block' }} />
                          <button type="button" onClick={() => setEditingReminderId(null)} style={{ ...btnSecondary, marginLeft: 8 }}>Done</button>
                        </span>
                      ) : (
                        <button type="button" onClick={() => setEditingReminderId(m.id)} style={btnSecondary}>
                          {m.reminderTime || '09:00'}
                        </button>
                      )}
                    </div>
                    {m.notes && <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 12, fontStyle: 'italic' }}>{m.notes}</div>}
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button type="button" onClick={() => openNovaForMed(m.name)} style={{ ...btnSecondary, color: 'var(--pink)' }}>
                        Nova explains this medication →
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Add medication modal */}
        {showAddForm && (
          <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 9999 }} onMouseDown={(e) => e.target === e.currentTarget && setShowAddForm(false)}>
            <div style={{ width: '100%', maxWidth: 480, background: 'white', borderRadius: 16, padding: 24, maxHeight: '90vh', overflowY: 'auto' }}>
              <h3 style={{ fontFamily: FONT_SERIF, fontSize: 20, marginBottom: 20 }}>Add medication</h3>
              <form onSubmit={handleAddMedication} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {[
                  { key: 'name', label: 'Name', type: 'text' },
                  { key: 'dose', label: 'Dose', type: 'text' },
                  { key: 'unit', label: 'Unit', type: 'select', options: UNITS },
                  { key: 'frequency', label: 'Frequency', type: 'text', placeholder: 'e.g. Daily, Twice daily' },
                  { key: 'route', label: 'Route', type: 'select', options: ROUTES },
                  { key: 'purpose', label: 'Purpose', type: 'text', placeholder: 'e.g. IVF stimulation' },
                  { key: 'startDate', label: 'Start date', type: 'date' },
                  { key: 'reminderTime', label: 'Reminder time', type: 'time' },
                ].map((f) => (
                  <div key={f.key}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>{f.label}</label>
                    {f.type === 'select' ? (
                      <select value={addForm[f.key] || ''} onChange={(e) => setAddForm((s) => ({ ...s, [f.key]: e.target.value }))} style={inputStyle}>
                        {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input type={f.type} value={addForm[f.key] || ''} onChange={(e) => setAddForm((s) => ({ ...s, [f.key]: e.target.value }))} style={inputStyle} placeholder={f.placeholder} />
                    )}
                  </div>
                ))}
                <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                  <button type="submit" style={btnPrimary}>Add</button>
                  <button type="button" onClick={() => setShowAddForm(false)} style={btnSecondary}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* IVF PROTOCOL (fertility only) */}
        {isFertility && activeMeds.length > 0 && (
          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 16 }}>IVF protocol</h2>
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 20, overflowX: 'auto' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {protocolDays.map(({ day, meds }) => (
                  <div key={day} style={{ display: 'flex', alignItems: 'center', gap: 16, minHeight: 40 }}>
                    <div style={{ minWidth: 80, fontWeight: 700, color: 'var(--text)' }}>Day {day}</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {meds.length === 0 ? (
                        <span style={{ color: 'var(--muted)', fontSize: 13 }}>—</span>
                      ) : (
                        meds.map((m) => (
                          <span key={m.id} style={{ padding: '6px 12px', borderRadius: 8, background: medColor(m.name), color: 'var(--text)', fontSize: 12, fontWeight: 600 }}>
                            {m.name} {m.dose} {m.units}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* MEDICATION HISTORY */}
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 16 }}>Medication history</h2>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            <select value={historyFilter.cycle} onChange={(e) => setHistoryFilter((f) => ({ ...f, cycle: e.target.value }))} style={{ ...inputStyle, width: 140 }}>
              <option value="all">All cycles</option>
              {pastCycles.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <input type="date" value={historyFilter.from} onChange={(e) => setHistoryFilter((f) => ({ ...f, from: e.target.value }))} style={{ ...inputStyle, width: 140 }} placeholder="From" />
            <input type="date" value={historyFilter.to} onChange={(e) => setHistoryFilter((f) => ({ ...f, to: e.target.value }))} style={{ ...inputStyle, width: 140 }} placeholder="To" />
          </div>
          {filteredHistory.length === 0 ? (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24, color: 'var(--muted)' }}>No past medications.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filteredHistory.map((m) => (
                <div key={m.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: 14, fontSize: 13 }}>
                  <strong>{m.name}</strong> · {m.dose} {m.units} · {m.cycleName} · {m.startedAt ? new Date(m.startedAt).toLocaleDateString() : '—'}
                </div>
              ))}
            </div>
          )}
        </section>

        {/* SIDE EFFECTS LOG */}
        <section style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 14, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 16 }}>Side effects log</h2>
          <p style={{ fontSize: 13, color: 'var(--muted)', marginBottom: 12 }}>Log side effects per medication. Nova reviews your log and can flag patterns.</p>
          <button type="button" onClick={() => setShowSideEffectForm(true)} style={{ ...btnSecondary, marginBottom: 16 }}>+ Log side effect</button>
          {sideEffects.length === 0 ? (
            <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 14, padding: 24, color: 'var(--muted)' }}>No side effects logged yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {sideEffects.map((se) => (
                <div key={se.id} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{se.medicationName || 'Medication'}</span>
                    <span style={{ fontSize: 12, color: 'var(--muted)' }}>{se.date}</span>
                  </div>
                  <div style={{ fontSize: 13 }}>{se.description}</div>
                  <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>Severity: {se.severity}/5</div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Side effect form modal */}
        {showSideEffectForm && (
          <div role="dialog" aria-modal="true" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, zIndex: 9999 }} onMouseDown={(e) => e.target === e.currentTarget && setShowSideEffectForm(false)}>
            <div style={{ width: '100%', maxWidth: 420, background: 'white', borderRadius: 16, padding: 24 }}>
              <h3 style={{ fontFamily: FONT_SERIF, fontSize: 18, marginBottom: 16 }}>Log side effect</h3>
              <form onSubmit={(e) => { e.preventDefault(); addSideEffect?.({ medicationId: sideEffectFormState.medicationId, medicationName: sideEffectFormState.medicationName || 'Unknown', description: sideEffectFormState.description, severity: sideEffectFormState.severity }); setShowSideEffectForm(false); setSideEffectFormState({ medicationId: '', medicationName: '', description: '', severity: 3 }); }}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Medication</label>
                  <select value={sideEffectFormState.medicationId} onChange={(e) => { const val = e.target.value; const m = activeMeds.find((x) => x.id === val); setSideEffectFormState((s) => ({ ...s, medicationId: val, medicationName: m?.name || (val ? '' : s.medicationName) })); }} style={inputStyle}>
                    <option value="">Select or type below...</option>
                    {activeMeds.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                  <input type="text" value={sideEffectFormState.medicationName} onChange={(e) => setSideEffectFormState((s) => ({ ...s, medicationName: e.target.value }))} placeholder="Or type medication name" style={{ ...inputStyle, marginTop: 6 }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Description</label>
                  <textarea value={sideEffectFormState.description} onChange={(e) => setSideEffectFormState((s) => ({ ...s, description: e.target.value }))} rows={3} style={inputStyle} required />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--muted)', display: 'block', marginBottom: 4 }}>Severity (1–5)</label>
                  <select value={sideEffectFormState.severity} onChange={(e) => setSideEffectFormState((s) => ({ ...s, severity: Number(e.target.value) }))} style={inputStyle}>
                    {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} {n <= 2 ? 'Mild' : n <= 4 ? 'Moderate' : 'Severe'}</option>)}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 12 }}>
                  <button type="submit" style={btnPrimary}>Log</button>
                  <button type="button" onClick={() => setShowSideEffectForm(false)} style={btnSecondary}>Cancel</button>
                </div>
              </form>
            </div>
          </div>
        )}
    </DashboardAppShell>
  );
}
